/**
 * csvDownload — byte-safe construction of the Blob that gets saved to disk for
 * CSV (and other text) downloads. METALARK-1183.
 *
 * Background: the server prepends a UTF-8 BOM (bytes EF BB BF) to its CSV
 * exports (METALARK-1100). That BOM is the only thing telling Excel on Windows
 * the file is UTF-8; without it Excel falls back to the ANSI code page and
 * accented text garbles ("informaciÃ³n" instead of "información").
 *
 * The client used to lose the BOM by reading the response with axios
 * `responseType: 'text'` — which decodes the bytes to a JS string and strips
 * the leading U+FEFF — and then rebuilding a Blob from that string, which
 * re-encodes to UTF-8 without the marker. The fix reads responses as Blobs
 * (`responseType: 'blob'`) so the raw bytes reach disk untouched. This helper
 * is the single place that turns whatever a download path is holding into the
 * Blob handed to the object-URL sink, so the invariant is enforced in one spot
 * and unit-testable without a bundler or a DOM.
 */

/**
 * Build the download Blob without dropping (or duplicating) the UTF-8 BOM.
 *
 * @param {Blob|string} data - the response body: a Blob (raw server bytes, BOM
 *   already present) or a string (e.g. a preview modal's parsed-back CSV text).
 * @param {string} [type] - MIME type used only when wrapping a string.
 * @returns {Blob}
 */
export function toBomSafeCsvBlob(data, type = 'text/csv') {
    // Already a Blob: it carries the raw response bytes (BOM included). Re-wrapping
    // would be pointless and risks a re-encode, so pass it straight through.
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
        return data;
    }
    // A string lost its BOM to text decoding (or never had one). Normalize any
    // stray leading BOM so we emit exactly one, then prepend it.
    const text = String(data ?? '').replace(/^\uFEFF/, '');
    return new Blob(['\uFEFF' + text], { type });
}

/**
 * Detect the async-export envelope an export endpoint returns in place of CSV
 * when the report is too large or too slow to come back through the gateway
 * (METALARK-1060 backend / METALARK-1066 client).
 *
 * Every export service reads its response with `responseType: 'blob'`, so a
 * `202 application/json` body arrives here as a Blob of JSON rather than as a
 * parsed object \u2014 which is exactly how it used to reach disk as a .csv full of
 * JSON. Sniffing the Blob's own MIME type keeps the detection here, so no
 * export service has to start returning the whole axios response.
 *
 * @param {Blob|string} data - the response body a download path is holding.
 * @returns {Promise<{jobId: string, downloadUrl?: string, fileName?: string}|null>}
 *   the envelope when the export went async, otherwise null (inline CSV).
 */
export async function readAsyncExportEnvelope(data) {
    if (typeof Blob === 'undefined' || !(data instanceof Blob)) return null;
    if (!String(data.type || '').includes('application/json')) return null;
    try {
        const envelope = JSON.parse(await data.text());
        // `status: 'accepted'` is the server's own discriminator. Requiring it
        // (rather than just "the body was JSON") means an error body or any
        // other JSON response falls through untouched.
        return envelope && envelope.status === 'accepted' && envelope.jobId ? envelope : null;
    } catch {
        return null;
    }
}
